/* ==========================================================================
   Future Aesthetics — <fa-announcement-slider>

   Below 990px the announcement slots become a swipeable, auto-advancing
   slider. Dragging is native: the track is a real scroll-snap container, so
   touch, trackpad and keyboard all work before any of this JS runs.

   Above 990px the track is a plain grid and nothing here applies.
   ========================================================================== */

(function () {
  'use strict';

  const MOBILE = '(max-width: 989px)';

  class FaAnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-announcement-track]');
      if (!this.track) return;

      this.slides = [...this.track.children];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.speed = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.wantsAutoplay = this.dataset.autoplay !== 'false';

      this.mq = window.matchMedia(MOBILE);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.buildArrows();

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      // Pause while the visitor is interacting, resume once they stop.
      ['pointerdown', 'touchstart', 'mouseenter', 'focusin'].forEach((evt) =>
        this.addEventListener(evt, () => this.stop(), { passive: true })
      );
      ['pointerup', 'touchend', 'mouseleave', 'focusout'].forEach((evt) =>
        this.addEventListener(evt, () => this.start(), { passive: true })
      );

      document.addEventListener('visibilitychange', () =>
        document.hidden ? this.stop() : this.start()
      );

      this.applyMode = this.applyMode.bind(this);
      this.mq.addEventListener('change', this.applyMode);
      this.applyMode();
    }

    disconnectedCallback() {
      this.stop();
      this.mq?.removeEventListener('change', this.applyMode);
      this.track?.removeEventListener('scroll', this.onScroll);
    }

    applyMode() {
      if (this.mq.matches) {
        this.arrows.forEach((a) => a.removeAttribute('hidden'));
        this.start();
      } else {
        this.arrows.forEach((a) => a.setAttribute('hidden', ''));
        this.stop();
        this.track.scrollTo({ left: 0 });
      }
    }

    buildArrows() {
      this.arrows = [
        ['prev', -1, 'Previous announcement', 'M11 3 5.5 8 11 13'],
        ['next', 1, 'Next announcement', 'M5 3l5.5 5L5 13'],
      ].map(([name, step, label, path]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `fa-announcement__arrow fa-announcement__arrow--${name}`;
        button.setAttribute('aria-label', label);
        button.innerHTML =
          `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">` +
          `<path d="${path}" fill="none" stroke="currentColor" stroke-width="1.8" ` +
          `stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        button.addEventListener('click', () => {
          this.go(this.index + step);
          this.stop();
        });
        this.appendChild(button);
        return button;
      });
    }

    onScroll() {
      // Derive the active slide from the scroll position so a manual drag
      // leaves the arrows stepping from where the visitor actually landed.
      const width = this.track.clientWidth || 1;
      this.index = Math.round(this.track.scrollLeft / width);
    }

    go(index) {
      const total = this.slides.length;
      this.index = ((index % total) + total) % total;
      this.track.scrollTo({
        left: this.track.clientWidth * this.index,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    start() {
      if (!this.wantsAutoplay || !this.mq.matches || this.reduceMotion.matches) return;
      this.stop();
      this.timer = window.setInterval(() => this.go(this.index + 1), this.speed);
    }

    stop() {
      window.clearInterval(this.timer);
    }
  }

  if (!customElements.get('fa-announcement-slider')) {
    customElements.define('fa-announcement-slider', FaAnnouncementSlider);
  }
})();
